<template>
    <v-dialog v-model="dialogSwitch" persistent scrollable width="720">
        <v-card rounded="xl">
            <v-card-title class="d-flex justify-space-between align-center pa-4 pb-0">
                <div>
                    <h4 class="text-h6 font-weight-bold">{{ isEditing ? 'Edit place' : 'Save a place' }}</h4>
                    <p class="text-body-2 text-medium-emphasis mb-0">
                        Draw a spot like Home or School. Everyone in your circle sees it on the map.
                    </p>
                </div>
                <v-btn density="compact" icon="mdi-close" variant="flat"
                       @click="$emit('update:modelValue', false)"/>
            </v-card-title>

            <v-divider class="mt-3"/>

            <v-card-text class="pa-4">
                <v-text-field v-model="name" :rules="[vRequired, vMaxLen]" ref="nameField"
                              label="Name (e.g. Home, School, Work)" variant="outlined"
                              density="comfortable" prepend-inner-icon="mdi-tag-outline"
                              class="mb-1" counter="40" maxlength="40"/>

                <v-chip-group class="mb-3">
                    <v-chip v-for="quickName in quickNames" :key="quickName"
                            size="small" variant="tonal" @click="name = quickName">
                        {{ quickName }}
                    </v-chip>
                </v-chip-group>

                <p class="text-body-2 font-weight-medium mb-2">Where is it?</p>
                <p class="text-caption text-medium-emphasis mb-2">
                    Search an address, then draw a circle or a custom shape on the map.
                </p>

                <v-card variant="outlined" rounded="lg" class="overflow-hidden">
                    <div style="height: 340px">
                        <l-map ref="mapContainer" :center="mapCenter" :zoom="15" @ready="mapIsReady">
                            <l-tile-layer :url="mapConfig.streetUrl"/>
                        </l-map>
                    </div>
                </v-card>

                <p v-if="!hasShape" class="text-caption text-medium-emphasis mt-2 mb-0">
                    Use the circle or polygon tool on the map to mark the place.
                </p>
                <p v-else class="text-caption text-success mt-2 mb-0">
                    <v-icon size="14" class="mr-1">mdi-check-circle</v-icon>
                    Place marked. You can redraw to adjust it.
                </p>

                <div class="mt-4">
                    <v-switch v-model="notify" color="primary" density="comfortable"
                              hide-details label="Notify my circle on arrive & leave"/>
                    <p class="text-caption text-medium-emphasis mb-0">
                        {{
                            notify
                                ? 'Everyone in your circle is notified when a tracker arrives or leaves here.'
                                : 'Just a saved spot - trackers here show "At ' + (name.trim() || 'this place') + '" on the map, with no alerts.'
                        }}
                    </p>
                </div>
            </v-card-text>

            <v-divider/>

            <v-card-actions class="pa-4">
                <v-spacer/>
                <v-btn variant="text" @click="$emit('update:modelValue', false)">Cancel</v-btn>
                <v-btn color="primary" variant="flat" :loading="saving" @click="save">
                    {{ isEditing ? 'Save changes' : 'Save place' }}
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script setup>
import "leaflet-draw";
import {mapConfig} from "@/composables/mapConfig";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet-draw/dist/leaflet.draw.css";
import "leaflet-geosearch/assets/css/leaflet.css";
import {LMap, LTileLayer} from "@vue-leaflet/vue-leaflet";
import {GeoSearchControl, OpenStreetMapProvider} from "leaflet-geosearch";
import {ref, computed, nextTick} from "vue";
import {send} from "@/composables/apiClient";
import {useAuthStore} from "@/stores/authStore";
import {useSnackbarStore} from "@/stores/snackBarStore";
import {apiErrorMessage} from "@/composables/useApiError";

const props = defineProps({
    modelValue: Boolean,
    // Optional {lat, lng} to seed the place at (e.g. a right-click on the live
    // map). When set, the dialog opens on that point with a default circle
    // already dropped, ready to name and save.
    initialPoint: {type: Object, default: null},
    // The place to EDIT (a geofence template: circle center+radius OR polygon
    // coordinates); null or absent = create a new place.
    place: {type: Object, default: null},
});
const emits = defineEmits(['update:modelValue', 'placeCreated']);

const isEditing = computed(() => !!(props.place && props.place.id));

// Normalise a saved polygon place's ring to [{lat,lng}]. Stored vertices use the
// DB keys latitude/longitude, but tolerate lat/lng/lon too.
function placePoints(p) {
    const coords = (p && p.polygon_coordinates) || [];
    return coords
        .map((c) => ({
            lat: Number(c.latitude ?? c.lat),
            lng: Number(c.longitude ?? c.lng ?? c.lon),
        }))
        .filter((pt) => Number.isFinite(pt.lat) && Number.isFinite(pt.lng));
}

// A saved place's centre for the initial map view: polygon first vertex, else
// circle centre, else null.
function placeCentre(p) {
    if (!p) return null;
    const pts = placePoints(p);
    if (pts.length) return pts[0];
    const clat = Number(p.center_lat);
    const clng = Number(p.center_lon ?? p.center_lng);
    if (Number.isFinite(clat) && Number.isFinite(clng)) return {lat: clat, lng: clng};
    return null;
}

const authStore = useAuthStore();
const {showSnackbar} = useSnackbarStore();

const dialogSwitch = computed({
    get: () => props.modelValue,
    set: (val) => emits('update:modelValue', val),
});

const quickNames = ['Home', 'School', 'Work', 'Gym', 'Office'];
const name = ref(props.place?.label ?? props.place?.name ?? '');
// Notify the circle on arrive & leave. The template's own enter_or_exit records
// the armed state (set by save); a new place defaults on (the common case).
const notify = ref(props.place ? (Number(props.place.enter_or_exit) || 0) > 0 : true);
const saving = ref(false);
const nameField = ref(null);
const mapContainer = ref();
const editCentre = placeCentre(props.place);
const mapCenter = ref(
    props.initialPoint
        ? [props.initialPoint.lat, props.initialPoint.lng]
        : editCentre
            ? [editCentre.lat, editCentre.lng]
            : [39.5, -98.35],
);

const vRequired = (v) => (!!v && !!v.trim()) || 'Please name this place.';
const vMaxLen = (v) => (!v || v.trim().length <= 40) || 'Keep it under 40 characters.';

// The shape the user drew: {type:'circle', center:{lat,lng}, radius} OR
// {type:'polygon', points:[{lat,lng}...]}. Null until they draw something.
const shape = ref(null);
const hasShape = computed(() => shape.value != null);

// Build a geocodable string from the signed-in user's saved address so a new
// place opens on their home. Returns null when nothing is on file.
function homeAddressQuery() {
    const u = authStore.user;
    if (!u) return null;
    const parts = [u.addr_1, u.addr_city, u.addr_state, u.addr_postalcode, u.addr_country]
        .map((s) => (s ?? '').toString().trim())
        .filter((s) => s.length);
    return parts.length ? parts.join(', ') : null;
}

function mapIsReady() {
    const map = mapContainer.value.leafletObject;
    map.attributionControl.remove();

    // Address search bar ("Enter address"): geocodes and recenters the map.
    const provider = new OpenStreetMapProvider({searchUrl: mapConfig.searchUrl, reverseUrl: mapConfig.reverseUrl});
    const geoSearch = new GeoSearchControl({
        style: "bar",
        autoClose: true,
        keepResult: true,
        showMarker: false,
        searchLabel: "Enter address",
        provider,
    });
    map.addControl(geoSearch);

    // Autofocus the "Enter address" box so the user can type a place straight
    // away (after the control renders).
    nextTick(() => setTimeout(() => {
        map.getContainer().querySelector('.leaflet-control-geosearch input')?.focus();
    }, 200));

    // Open the map on the account's home address instead of the world default,
    // unless we were seeded with a specific point (right-click "Save place here").
    const homeQuery = homeAddressQuery();
    if (homeQuery && !props.initialPoint && !isEditing.value) {
        provider.search({query: homeQuery}).then((results) => {
            if (results?.length) map.setView([results[0].y, results[0].x], 15);
        }).catch(() => { /* geocode failed; leave the default view */ });
    }

    // Draw toolbar: a circle or a custom polygon (one shape at a time).
    const drawnItems = new L.FeatureGroup();
    map.addLayer(drawnItems);
    const drawControl = new L.Control.Draw({
        draw: {
            circle: true,
            polygon: {
                showArea: true,
                allowIntersection: false,
                drawError: {
                    color: '#9d1212',
                    message: '<strong>Oops<strong> shapes can\'t cross themselves',
                },
            },
            marker: false,
            polyline: false,
            rectangle: false,
            circlemarker: false,
        },
        edit: {featureGroup: drawnItems},
    });
    map.addControl(drawControl);

    // The tracker firmware supports at most 32 geofence vertices (the API also
    // caps at 32), so reject a polygon with more.
    const MAX_POLYGON_POINTS = 32;
    const captureLayer = (type, layer) => {
        if (type === 'circle') {
            shape.value = {
                type: 'circle',
                center: {lat: layer.getLatLng().lat, lng: layer.getLatLng().lng},
                radius: layer.getRadius(),
            };
            return true;
        }
        if (type === 'polygon') {
            const points = layer.getLatLngs()[0].map((p) => ({lat: p.lat, lng: p.lng}));
            if (points.length > MAX_POLYGON_POINTS) {
                showSnackbar(`A place can have at most ${MAX_POLYGON_POINTS} points. Draw a simpler shape.`, 'error');
                return false;
            }
            shape.value = {type: 'polygon', points};
            return true;
        }
        return false;
    };

    map.on('draw:created', function (e) {
        // Only one place per dialog: clear any previous shape before adding.
        drawnItems.eachLayer((layer) => drawnItems.removeLayer(layer));
        // Only keep the drawn layer if it was accepted (a >32-point polygon is
        // rejected so it isn't saved).
        if (captureLayer(e.layerType, e.layer)) {
            drawnItems.addLayer(e.layer);
        }
    });

    map.on('draw:edited', function (e) {
        e.layers.eachLayer((layer) => {
            if (layer instanceof L.Circle) captureLayer('circle', layer);
            else if (layer instanceof L.Polygon) captureLayer('polygon', layer);
        });
    });

    map.on('draw:deleted', function () {
        drawnItems.eachLayer((layer) => drawnItems.removeLayer(layer));
        shape.value = null;
    });

    // Editing an existing place: draw its saved shape so the user can adjust it
    // (drag vertices via the edit tool) or redraw it entirely. A geofence
    // template is either a polygon (polygon_coordinates) or a native circle
    // (type 0 with center_lat/center_lon/radius).
    if (isEditing.value) {
        const pts = placePoints(props.place);
        const clat = Number(props.place.center_lat);
        const clng = Number(props.place.center_lon ?? props.place.center_lng);
        const radius = Number(props.place.radius);
        const hasCircle = Number.isFinite(clat) && Number.isFinite(clng) && radius > 0;
        // Branch on the template's type FIRST, not on which geometry is present:
        // a place converted polygon->circle keeps its (now inert) polygon rows, so
        // a points-first check would re-load the stale polygon shape for what is
        // really a circle. type 0 = native circle, type 1 = polygon.
        const isCircle = Number(props.place.type) === 0;
        if (isCircle && hasCircle) {
            const circle = L.circle([clat, clng], {radius});
            drawnItems.addLayer(circle);
            captureLayer('circle', circle);
            map.setView([clat, clng], 15);
        } else if (pts.length >= 3) {
            const poly = L.polygon(pts.map((p) => [p.lat, p.lng]));
            drawnItems.addLayer(poly);
            shape.value = {type: 'polygon', points: pts};
            map.fitBounds(poly.getBounds(), {padding: [20, 20]});
        } else if (hasCircle) {
            const circle = L.circle([clat, clng], {radius});
            drawnItems.addLayer(circle);
            captureLayer('circle', circle);
            map.setView([clat, clng], 15);
        }
    }

    // Seeded from a right-click "Save place here": drop a default circle at the
    // clicked point so the user only has to name it (radius stays editable).
    if (props.initialPoint) {
        const seed = L.circle([props.initialPoint.lat, props.initialPoint.lng], {radius: 200});
        drawnItems.addLayer(seed);
        captureLayer('circle', seed);
        map.setView([props.initialPoint.lat, props.initialPoint.lng], 16);
    }
}

// Native geometry payload for the geofence template: a circle keeps its centre
// + radius; a polygon its ring. Returns null when the shape is invalid.
function geometryBody() {
    if (!shape.value) return null;
    if (shape.value.type === 'circle') {
        const r = Number(shape.value.radius);
        if (!(r > 0)) return null;
        return {
            type: 0,
            center_lat: shape.value.center.lat,
            center_lon: shape.value.center.lng,
            radius: r,
        };
    }
    const pts = (shape.value.points ?? []).filter(
        (p) => Number.isFinite(p.lat) && Number.isFinite(p.lng),
    );
    if (pts.length < 3) return null;
    return {type: 1, polygon_coordinates: pts.map((p) => ({lat: p.lat, lng: p.lng}))};
}

// All the circle's monitoring configs across every page (best-effort). Paged so
// a config beyond page 1 is not missed (which would double-create on arm).
async function fetchAllConfigs() {
    const all = [];
    let page = 1;
    let last = 1;
    try {
        do {
            const resp = await send('get', 'monitoring/configs', {page});
            const pg = resp?.data?.data;
            all.push(...(pg?.data ?? []));
            last = Number(pg?.last_page) || page;
            page++;
        } while (page <= last && page <= 20);
    } catch {
        return all;
    }
    return all;
}

// The geofence alert config already attached to [templateId], or null.
function geofenceConfigFor(configs, templateId) {
    return configs.find(
        (c) => c.monitoring_alertable_type === 'App\\Models\\MonitoringGeofenceTemplate'
            && Number(c.monitoring_alertable_id) === Number(templateId),
    ) ?? null;
}

// Seed a fresh notification group (one per config - the config-delete path
// deletes the group, so a shared group would break sibling configs).
async function seedGroupId() {
    const resp = await send('get', 'monitoring/single-group-members');
    const g = Number(resp?.data?.data?.group?.id);
    return Number.isFinite(g) && g > 0 ? g : null;
}

async function allAssetIds() {
    const resp = await send('get', 'personal-trackers/simple-list');
    const list = resp?.data?.data ?? [];
    return list.map((d) => d.asset_id).filter((id) => id != null);
}

// Bring the place's alerting into line with the notify switch. The template's
// enter_or_exit (set on the template save above) is the on/off state; arming
// additionally needs a monitoring config to exist. We only ever CREATE a config
// (never delete one here) - deleting a config cascade-deletes the place's
// geofence template, so a disarm just leaves the (now enter_or_exit=0) template
// with a dormant config. Throws on failure so the caller can report it.
async function reconcileAlertConfig(templateId, label) {
    if (!notify.value) return; // disarm: template enter_or_exit=0 already saved
    const existing = geofenceConfigFor(await fetchAllConfigs(), templateId);
    if (existing) return; // already has a config
    const groupId = await seedGroupId();
    const assets = await allAssetIds();
    if (!groupId || !assets.length) {
        throw new Error('Alert setup needs a notification group and at least one tracker.');
    }
    await send('post', 'monitoring/configs/personal', {
        name: label,
        type: 'Geofence',
        notificationGroup: groupId,
        assets,
        alert: {id: templateId, enter_or_exit: 3},
        schedule: {all_times_enabled: true},
        // A Place alert always covers the whole circle, so a tracker added later
        // inherits it too.
        applies_to_all: true,
    });
}

async function save() {
    if (!name.value || !name.value.trim()) {
        showSnackbar('Please name this place.', 'error');
        return;
    }
    if (name.value.trim().length > 40) {
        showSnackbar('Keep the name under 40 characters.', 'error');
        return;
    }
    const geometry = geometryBody();
    if (!geometry) {
        showSnackbar('Draw a circle or a larger shape on the map to mark the place.', 'error');
        return;
    }
    const companyId = authStore.user?.company?.id;
    if (!companyId) {
        showSnackbar('No circle selected.', 'error');
        return;
    }

    saving.value = true;
    const label = name.value.trim();
    const body = {
        company_id: companyId,
        label,
        trigger_delay: 0,
        minimum_violation_time: 0,
        // The template's own enter_or_exit is the arrive/leave on/off state.
        enter_or_exit: notify.value ? 3 : 0,
        ...geometry,
    };
    // On edit, update() only re-writes the polygon child rows when location_changed
    // is truthy. Without it a reshaped/redrawn polygon - or a circle->polygon
    // change - is silently discarded and the OLD boundary keeps firing alerts.
    // create() re-writes coordinates unconditionally, so the flag is edit-only.
    if (isEditing.value) {
        body.location_changed = true;
    }

    // Step 1: save the place (geofence template). A failure here is a real save
    // failure - do NOT emit, so the user can retry without a partial place.
    let templateId = null;
    try {
        const response = isEditing.value
            ? await send('put', `monitoring/geofence-templates/${props.place.id}`, body)
            : await send('post', 'monitoring/geofence-templates', body);
        if (response.data.success === false) {
            showSnackbar(response.data.message || 'Could not save the place.', 'error');
            saving.value = false;
            return;
        }
        templateId = response?.data?.data?.id ?? props.place?.id ?? null;
    } catch (error) {
        // This screen writes monitoring/geofence-templates, so the cap it can
        // hit is max_ZONES, not max_places - the places cap lives on
        // companies/locations, which this client never calls. Either way a 402
        // here names the cap and points at whoever can lift it, instead of
        // repeating one generic sentence. Any other status still shows the
        // server message.
        showSnackbar(
            apiErrorMessage(error, 'Could not save the place.', {canUpgrade: authStore.isCircleOwner}).message,
            'error',
        );
        saving.value = false;
        return;
    }

    // Step 2: reconcile the alert config (only with a real template id - never
    // POST a config with alert.id=null, which would create a geometry-less zone).
    // The place IS saved now, so a failure here must not read as a save failure
    // or prompt a re-save (which would duplicate the place).
    let alertError = null;
    if (templateId) {
        try {
            await reconcileAlertConfig(templateId, label);
        } catch (error) {
            alertError = error;
            console.log(error);
        }
    }

    // Arming failed but the template was saved with enter_or_exit=3 and has no
    // config, so nothing would fire - revert it to 0 so the "Alerts on" badge is
    // honest. Best-effort; the snackbar still explains.
    if (alertError && notify.value && templateId) {
        try {
            await send('put', `monitoring/geofence-templates/${templateId}`, {...body, enter_or_exit: 0});
        } catch (e) {
            console.log(e);
        }
    }
    saving.value = false;

    if (!templateId || alertError) {
        const reason = (alertError && alertError.message && !alertError.response)
            ? ` (${alertError.message})` : '';
        showSnackbar(`Place saved, but its alert couldn't be set up${reason}. Reopen the place to try again.`, 'error');
    } else {
        showSnackbar(notify.value
            ? (isEditing.value ? 'Place updated. Your circle is alerted for this place.' : 'Place saved. Your circle is alerted for this place.')
            : (isEditing.value ? 'Place updated.' : 'Place saved.'));
    }
    emits('placeCreated');
}
</script>
